/* eslint-disable react-refresh/only-export-components */
//
// PixelTestHarness — DEV-ONLY pixel-perfect comparison surface.
//
// Mounts a single dashboard section in a fixed-width container with the v5
// design's *exact* mock data, so a Playwright screenshot can be pixel-matched
// against the scaled source crop. This is the only way to a true ≤0.5% diff:
// the live dashboard differs from the source on geometry (1120px card vs the
// design's 1340px) AND on data (real patient vs the design's "Kelly Freemont"),
// and pixelmatch counts every differing pixel. Here we control both.
//
// Guarded by import.meta.env.DEV — renders nothing in a production build.
//
// Route:  /__pixel-test/:section   (see router.tsx)
// Usage:  http://demo.mld.test/__pixel-test/health-profile
//
// Each section is wrapped in fresh providers that SHADOW the app-level ones:
//   • AuthProvider  — mock authenticated "Kelly Freemont"
//   • Redux Provider — preloaded patient + subscription state
//   • QueryClient   — primed via setQueryData, staleTime Infinity (no refetch)
//
import React from 'react';
import {useParams} from 'react-router-dom';
import {Provider} from 'react-redux';
import {combineReducers, configureStore} from '@reduxjs/toolkit';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {AuthProvider} from '@/modules/auth/hooks/useAuth';
import {USER_DATA_KEY} from '@/modules/patient-dashboard/hooks/useTestsQuery';

import patientReducer from '@/slices/patientSlice';
import kitReducer from '@/slices/kitSlice';
import bannerReducer from '@/slices/bannerSlice';
import panelReducer from '@/slices/panelSlice';

import HealthProfileCard from '@/modules/patient-dashboard/components/HealthProfileCard';
import BiomarkersNeedAttentionCard from '@/modules/patient-dashboard/components/BiomarkersNeedAttentionCard';
import CustomBioMatchHero from '@/modules/biomatch/components/CustomBioMatchHero';
// The design's avatar is a stock photo on a lavender circle. Cropped from the
// source design at render scale so the harness can verify avatar chrome (size,
// position, ring) without the photo dominating the diff. In production this is
// the patient's own uploaded photo — see known-deviations.md.
import kellyAvatar from './kelly-avatar.png';

// ─── The design's mock identity (shared across sections) ──────────────
const KELLY = {
    id: 1,
    firstName: 'Kelly',
    lastName: 'Freemont',
    fullName: 'Kelly Freemont',
    birthdate: '1999-09-09',
};

const KELLY_USER = {
    isLoggedIn: true,
    token: 'pixel-test',
    userInfo: {
        id: KELLY.id,
        firstName: KELLY.firstName,
        lastName: KELLY.lastName,
        // Real wire shape: a Postgres `date` reaches the client as UTC midnight.
        // The card formats it via the shared UTC-safe helper, so this renders
        // 09/09/99 in every timezone — no noon fudge needed.
        birthday: '1999-09-09T00:00:00.000Z',
        gender: 'F',
    },
};

// Build a fresh store mirroring the app's reducer map, with the patient +
// subscription state the design implies (Kelly, Free Membership).
const rootReducer = combineReducers({
    patient: patientReducer,
    kit: kitReducer,
    banner: bannerReducer,
    panels: panelReducer,
});

function makeStore() {
    return configureStore({
        reducer: rootReducer,
        // Partial preloaded state — only the slices the sections read. This is a
        // dev-only harness, so we isolate the cast rather than enumerate every
        // slice's full initial state. Subscription now comes from the React Query
        // `user-data` cache (see useSubscription), not Redux.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        preloadedState: {
            patient: {currentPatient: KELLY, subPatients: []},
        } as any,
    });
}

// A QueryClient that never refetches, so the primed mock data is what renders.
function makeQueryClient(seed: (qc: QueryClient) => void) {
    const qc = new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: Infinity,
                gcTime: Infinity,
                retry: false,
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            },
        },
    });
    seed(qc);
    return qc;
}

// ─── v11 right-column fixture ─────────────────────────────────────────
// The design's 8 flagged markers, and the assay list its matched product
// bundles ("Included In Your Panel").
const V11_MARKERS: Array<[string, 'high' | 'low']> = [
    ['HbA1c', 'high'],
    ['LDL', 'high'],
    ['HDL', 'high'],
    ['Vitamin D', 'low'],
    ['Estradiol', 'low'],
    ['Vitamin B12', 'low'],
    ['Cortisol', 'low'],
    ['Iron', 'low'],
];

const V11_PANEL = [
    'Cholesterol',
    'HDL Cholesterol',
    'LDL Cholesterol',
    'Triglycerides',
    'HbA1c',
    'C-Reactive Protein (hs-CRP)',
    'Uric Acid',
    'Creatinine',
    'eGFR',
    'BUN / Creatinine Ratio',
    'BUN',
    'ALT (SGPT)',
    'GGT',
    'Total Bilirubin',
    'Direct Bilirubin',
    'Albumin',
    'Total Protein',
];

// ─── Section registry ─────────────────────────────────────────────────
// Each entry: the fixed container width (the render anchor that matches the
// scaled-source crop), the mock store, the QueryClient seed, and the element.
interface SectionDef {
    width: number;
    subscription: boolean;
    seed: (qc: QueryClient) => void;
    render: () => React.ReactNode;
}

const SECTIONS: Record<string, SectionDef> = {
    'health-profile': {
        width: 1120,
        subscription: false, // design shows "Free Membership"
        seed: (qc) => {
            qc.setQueryData(['health-profile'], {
                profilePhotoUrl: kellyAvatar,
                heightInches: 68,
                weightLbs: 122,
                waistInches: 32,
            });
            // biometrics-latest keyed by viewingUserId (Kelly = 1)
            qc.setQueryData(['biometrics-latest', KELLY.id], {
                height: {measurement: 'height', value: '68', units: 'in'},
                weight: {measurement: 'weight', value: '122', units: 'lb'},
                waist: {measurement: 'waist', value: '32', units: 'in'},
                bmi: {measurement: 'bmi', value: '19.7', units: ''},
                // No bp_systolic / bp_diastolic — design shows no BP line.
            });
            // biometrics-history: most-recent-first; component slices(0,3).reverse()
            // → renders oldest→newest left to right.
            //   Weight sparkline 133 → 128 → 122  (entries: 122,128,133)
            //   BMI    sparkline  24 → 21.5 → 19.7 (entries: 19.7,21.5,24)
            const wk = (value: string, recorded_on: string) => ({
                id: 0,
                user_id: KELLY.id,
                organization_id: null,
                measurement: 'weight',
                value,
                units: 'lb',
                loinc: null,
                recorded_on,
                source: 'mock',
                validated: true,
                source_ref_id: null,
                created_at: recorded_on,
            });
            const bk = (value: string, recorded_on: string) => ({
                ...wk(value, recorded_on),
                measurement: 'bmi',
                units: '',
            });
            qc.setQueryData(['biometrics-history', KELLY.id], {
                weight: [wk('122', '2026-04-01'), wk('128', '2026-03-01'), wk('133', '2026-02-01')],
                bmi: [bk('19.7', '2026-04-01'), bk('21.5', '2026-03-01'), bk('24', '2026-02-01')],
            });
        },
        render: () => <HealthProfileCard />,
    },

    // v11 right sidebar: "Biomarkers Need Attention" + the BioMatch kit card.
    // The design shows 8 flagged markers (3 out of range, 5 borderline) and a
    // matched "Cardiac Risk Follow-Up Test Kit" at $175.99 / $140.79 with Plus.
    'right-column': {
        // The PSD's right column is 505px wide at the 1920px artboard.
        width: 505,
        subscription: false,
        seed: (qc) => {
            // Results drive severity: above the range → High (pink, "out of
            // range"), below it → Low (orange, "borderline"). Values are
            // deliberately extreme — several of these biomarkers (LDL,
            // Cortisol) carry custom zone configs, and a merely-out-of-range
            // value lands in their Borderline/Normal bands instead.
            const results = V11_MARKERS.map(([name, level], i) => ({
                test_name: name,
                test_code: `T${i}`,
                loinc: null,
                result: level === 'high' ? '999' : '0.01',
                refRange: '10-20',
                panel_name: '',
                panel_code: '',
                units: '',
                notes: '',
                statusDate: '2026-04-24',
            }));
            qc.setQueryData([USER_DATA_KEY, KELLY.id, null], {
                orders: [
                    {
                        id: 1,
                        createdDate: '2026-04-24',
                        statusDate: '2026-04-24',
                        firstName: KELLY.firstName,
                        lastName: KELLY.lastName,
                        results,
                    },
                ],
                categoryScores: {},
                subscription: false,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any);
            qc.setQueryData(['biomatch', 'dashboard', 'mine'], {
                curated_cards: [],
                mylabs_plus_member_discount_pct: 20,
                custom_package: {
                    patient_first_name: KELLY.firstName,
                    assays: [],
                    matched_markers: V11_MARKERS.map(([name], i) => ({
                        code: `M${i}`,
                        name,
                        loinc: null,
                        sourced_from: [],
                        bundled_panel_code: null,
                    })),
                    products: [
                        {
                            id: 1,
                            name: 'Cardiac Risk Follow-Up Test Kit',
                            description:
                                'A personalized cardiometabolic assessment that provides deeper insight into ' +
                                'your heart, blood sugar, kidney, liver, and metabolic health to help identify ' +
                                'potential health risks.',
                            image_url: null,
                            price_cents: 17599,
                            member_price_cents: null,
                            category: 'kit',
                            default_cadence_days: null,
                            cadence_label: null,
                            supply_per_shipment: null,
                            member_monthly_cents: 0,
                            price_overridden: false,
                            sourced_from: [],
                        },
                    ],
                    product_included_assays: V11_PANEL.map((name, i) => ({code: `P${i}`, name, loinc: null})),
                    bundled_panels: [],
                    pricing: {
                        one_time_cents: 17599,
                        member_monthly_cents: 5866,
                        kit_breakdown: {},
                    },
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any);
        },
        render: () => (
            <div className="space-y-6">
                <BiomarkersNeedAttentionCard />
                <CustomBioMatchHero />
            </div>
        ),
    },
};

const PixelTestHarness: React.FC = () => {
    const {section = ''} = useParams();

    if (!import.meta.env.DEV) {
        return <div style={{padding: 40}}>Pixel-test harness is dev-only.</div>;
    }

    const def = SECTIONS[section];
    if (!def) {
        return (
            <div style={{padding: 40, fontFamily: 'monospace'}}>
                Unknown section "{section}". Available: {Object.keys(SECTIONS).join(', ')}
            </div>
        );
    }

    const store = makeStore();
    const qc = makeQueryClient((client) => {
        // Subscription now lives in the React Query `user-data` cache
        // (useSubscription), not Redux — seed it so the card renders the
        // section's intended membership state. Key must match useTestsQuery's
        // `[USER_DATA_KEY, userId, impersonateId]` for the mock Kelly user
        // (id 1, non-JWT token → impersonateId null).
        client.setQueryData([USER_DATA_KEY, KELLY.id, null], {
            orders: [],
            categoryScores: {},
            subscription: def.subscription,
        });
        def.seed(client);
    });

    return (
        // Solid page-bg backdrop so the screenshot matches the design's beige.
        <div style={{background: '#F0F1EB', minHeight: '100vh', padding: 0}}>
            <AuthProvider user={KELLY_USER} initialCheckToken={false}>
                <Provider store={store}>
                    <QueryClientProvider client={qc}>
                        {/* Fixed-width anchor — matches the scaled-source crop width. */}
                        <div style={{width: def.width}} data-pixel-test-root>
                            {def.render()}
                        </div>
                    </QueryClientProvider>
                </Provider>
            </AuthProvider>
        </div>
    );
};

export default PixelTestHarness;
